import React, { useState, useMemo, useCallback } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import toast from "react-hot-toast";

const libraries = ["places"];

const LocationPicker = ({ onLocationSet, isModal }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [selected, setSelected] = useState(null); // { lat, lng }
  const [address, setAddress] = useState("");

  // Default center (Nagpur)
  const defaultCenter = useMemo(() => ({ lat: 21.1458, lng: 79.0882 }), []);
  const [center, setCenter] = useState(defaultCenter);

  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setSelected({ lat, lng });
    
    // Reverse geocoding to get address from click
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      } else {
        toast.error("Failed to get address details");
      }
    });
  }, []);

  const confirmLocation = () => {
    if (!selected) {
      toast.error("Please select a location");
      return;
    }
    const locationData = {
      lat: selected.lat,
      lng: selected.lng,
      address: address,
    };
    localStorage.setItem("gutful_user_location", JSON.stringify(locationData));
    onLocationSet(locationData);
    toast.success("Location saved!");
  };

  const handleCurrentLocation = () => {
     if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const coords = { lat: latitude, lng: longitude };
          setCenter(coords);
          setSelected(coords);

          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: coords }, (results, status) => {
            if (status === "OK" && results[0]) {
              setAddress(results[0].formatted_address);
            }
          });
        },
        () => {
          toast.error("Failed to get location");
        }
      );
    } else {
      toast.error("Geolocation not supported");
    }
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className={`${!isModal ? "min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4" : "p-6"}`}>
      <div className={`${!isModal ? "bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full" : "w-full"}`}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#0B9444" }}>
            Select Delivery Location
          </h2>
          <p className="text-gray-600">
            Search for your address or pin it on the map
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 flex-1 w-full">
            <PlacesAutocomplete
              setSelected={setSelected}
              setCenter={setCenter}
              setAddress={setAddress}
            />
            <button
                onClick={handleCurrentLocation}
                className="w-full sm:w-auto p-3 rounded-lg border-2 font-semibold transition hover:bg-green-50 flex items-center justify-center gap-2"
                style={{ borderColor: "#0B9444", color: "#0B9444" }}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="sm:hidden">Use Current Location</span>
              </button>
            </div>

          <div className="h-64 w-full rounded-lg overflow-hidden border border-gray-300 relative z-0">
            <GoogleMap
              zoom={15}
              center={center}
              mapContainerClassName="w-full h-full"
              onClick={onMapClick}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
              }}
            >
              {selected && <Marker position={selected} />}
            </GoogleMap>
          </div>

          <div className="flex gap-3">
            

            <button
              onClick={confirmLocation}
              className="flex-1 py-3 rounded-lg text-white font-semibold transition hover:opacity-90"
              style={{ backgroundColor: "#0B9444" }}
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlacesAutocomplete = ({ setSelected, setCenter, setAddress }) => {
  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete();

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();
    setAddress(address);

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      setSelected({ lat, lng });
      setCenter({ lat, lng });
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  return (
    <div className="relative w-full">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={!ready}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none focus:ring-green-500 focus:border-transparent"
        placeholder="Search for your area..."
      />
      {status === "OK" && (
        <ul className="absolute z-[1000] w-full bg-white border border-gray-200 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto">
          {data.map(({ place_id, description }) => (
            <li
              key={place_id}
              onClick={() => handleSelect(description)}
              className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-b-0"
            >
              {description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationPicker;
